import { useCallback, useEffect, useRef } from "react";
import type { MouseEvent, PointerEvent } from "react";

/**
 * Tap and hold, on one tile.
 *
 * A till is a touch terminal, so the two gestures a cashier already has are a
 * tap and a hold, and the menu grid uses both: a tap adds the item, a hold
 * opens the sheet where quantity, note and options live
 * (`08-pos-terminal-and-qr.md` § A, step 2).
 *
 * This lives in a hook rather than inline on the tile because every mechanic
 * below is a defect if a call site forgets it, and none of them are obvious
 * from the outside. It is also the part of this screen that cannot be unit
 * tested — the repo has no DOM environment — so keeping it in one small,
 * documented place is the substitute.
 */
export type PressHandlers = {
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  onPointerUp: () => void;
  onPointerLeave: () => void;
  onPointerCancel: () => void;
  onContextMenu: (event: MouseEvent<HTMLElement>) => void;
  onClick: (event: MouseEvent<HTMLElement>) => void;
};

export function usePress({
  onTap,
  onHold,
  /**
   * The usual floor. Shorter and a deliberate tap starts opening sheets;
   * longer and the hold feels like the tile has stopped responding.
   */
  holdMs = 500,
  /** A finger is never still. Below this a press is a press, not a drag. */
  moveTolerance = 10,
}: {
  onTap: () => void;
  onHold: () => void;
  holdMs?: number;
  moveTolerance?: number;
}): PressHandlers {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const held = useRef(false);
  /** Set when the gesture stopped being a press — a drag, or the browser took it. */
  const abandoned = useRef(false);

  const stop = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  // A tile can unmount while held — a search that narrows under the finger is
  // enough — and a timer that outlives it would open a sheet over the results.
  useEffect(() => stop, [stop]);

  const cancel = useCallback(() => {
    abandoned.current = true;
    stop();
  }, [stop]);

  return {
    onPointerDown: (event) => {
      stop();
      held.current = false;
      // Only the primary button gestures. A right-click belongs to
      // `onContextMenu`, and letting it through here would both open the sheet
      // and, on release, add the item.
      abandoned.current = event.button !== 0;
      if (abandoned.current) return;
      origin.current = { x: event.clientX, y: event.clientY };
      timer.current = setTimeout(() => {
        held.current = true;
        onHold();
      }, holdMs);
    },

    onPointerMove: (event) => {
      const from = origin.current;
      if (from === null || abandoned.current) return;
      if (Math.hypot(event.clientX - from.x, event.clientY - from.y) > moveTolerance) cancel();
    },

    // The browser fires `pointercancel` the moment it claims the gesture for
    // scrolling, which is exactly a finger that started on a tile and flicked.
    onPointerCancel: cancel,
    onPointerLeave: cancel,

    onPointerUp: () => {
      stop();
      // The hold already did its work on the timer; releasing must not also add.
      const wasHeld = held.current;
      // Reset before the next gesture rather than at its start, so a hold does
      // not leave a flag standing that would swallow a later Menu-key press.
      held.current = false;
      if (wasHeld || abandoned.current) return;
      onTap();
    },

    onContextMenu: (event) => {
      // Always: this is what suppresses the iOS callout and the Android menu
      // that a long press would otherwise raise over the sheet.
      event.preventDefault();
      // Right-click, or the keyboard Menu key — the routes to the sheet that a
      // touch gesture cannot offer. On touch the timer has already fired by
      // now, and opening again would be the same hold counted twice; claiming
      // the gesture here stops a pending timer doing it the other way round.
      const opened = held.current;
      held.current = true;
      stop();
      if (!opened) onHold();
    },

    onClick: (event) => {
      // Keyboard only. A click synthesised by Enter or Space carries
      // `detail === 0`; one that followed a real pointer does not, and that one
      // is already accounted for by `onPointerUp`.
      if (event.detail === 0) onTap();
    },
  };
}
